import type { CustomServerData } from 'src/types/sku';

import { CUSTOM_SERVER_DATA_ID } from './constants';

export type Environment = 'local' | 'development' | 'production';
interface ConfigValue {
  local: string | boolean;
  development: string | boolean;
  production: string | boolean;
}
type ConfigList = Record<string, ConfigValue>;

let environment: Environment | null = null;
const configValList: Record<string, string | boolean> = {};

const configVals: ConfigList = {
  ADROLL_ADVERTISER_ID: {
    local: '12345',
    development: '12345',
    production: '74FVLGFAUZDE7OXH56CDDT',
  },
  ADROLL_PIXEL_ID: {
    local: '67890',
    development: '67890',
    production: 'XKM5WQ27BZG2TJDUV5ECWG',
  },
  LOOPA_PUBLISHER_ID: {
    local: '12345',
    development: '12345',
    production: '5e33b0a86dc4161384bdbd87',
  },
  ADVERTISER_CENTRE_URL: {
    local: 'http://advertiser.sbus.rat',
    development: 'https://advertiser.qa.seekbusiness.com.au',
    production: 'https://advertiser.seekbusiness.com.au',
  },
  INFORMANT_URL: {
    local: 'https://qa.seekbusiness.com.au/api/informant',
    development: 'https://qa.seekbusiness.com.au/api/informant',
    production: 'https://www.seekbusiness.com.au/api/informant',
  },
  GPT_BASE_URL: {
    local: '/18718623/CommercialAU/sbushomepagetile',
    development: '/18718623/CommercialAU/sbushomepagetile',
    production: '/18718623/CommercialAU/sbushomepagetile',
  },
  GRAPHQL_API_URL: {
    local: 'https://qa.seekbusiness.com.au/graphql',
    development: 'https://qa.seekbusiness.com.au/graphql',
    production: 'https://www.seekbusiness.com.au/graphql',
  },
  SUBSCRIPTIONS_API_URL: {
    local: 'https://qa.seekbusiness.com.au/api/subscriptions',
    development: 'https://qa.seekbusiness.com.au/api/subscriptions',
    production: 'https://www.seekbusiness.com.au/api/subscriptions',
  },
  HEADER_SERVER_BASE: {
    local: 'https://qa.seekbusiness.com.au',
    development: 'https://qa.seekbusiness.com.au',
    production: 'https://www.seekbusiness.com.au',
  },
  PUBLIC_PATH_BASE: {
    local: 'http://localhost:3000',
    development: 'https://qa.seekbusiness.com.au/resources',
    production: 'https://www.seekbusiness.com.au/resources',
  },
  HEAP_ANALYTICS_KEY: {
    local: '2309915322',
    development: '2309915322',
    production: '2596402009',
  },
  IMAGE_SERVER_BASE: {
    local: 'https://images.qa.seekbusiness.com.au',
    development: 'https://images.qa.seekbusiness.com.au',
    production: 'https://images.seekbusiness.com.au',
  },
  SERVER_BASE_URL: {
    local: 'http://localhost:4001',
    development: '',
    production: '',
  },
  SEGMENT_CDP_KEY: {
    local: 'hp1LdAveolKAKqn90OolAQNWVqHmrLz2',
    development: 'hp1LdAveolKAKqn90OolAQNWVqHmrLz2',
    production: 'CVmAWYCeGnxGLt3KPj3rqHjMumkLR75o',
  },
  STRIPE_API_KEY: {
    local: 'pk_test_oajvLaMCMFwVfNgVJHUFUkEA',
    development: 'pk_test_oajvLaMCMFwVfNgVJHUFUkEA',
    production: 'pk_live_7Xp1dpYNfP5v2pqcUpbuK3kU',
  },
  NAVBAR_REDESIGN_EXPERIMENT_OPTIMIZE_ID: {
    local: 'vFN3r9JpTDaSL_BYt1XaYA',
    development: 'B-bhCrrLQB26_KGIq3eE5Q',
    production: 'daPKBAcJSiyrvljNwHL35Q',
  },
};

const setEnvironment = (passedEnvironment: Environment) => {
  environment = passedEnvironment;
  configValList.ENVIRONMENT = passedEnvironment;
  for (const key of Object.keys(configVals)) {
    configValList[key] = configVals[key][environment];
  }
};

interface FullConfig {
  ADROLL_ADVERTISER_ID: string;
  ADROLL_PIXEL_ID: string;
  LOOPA_PUBLISHER_ID: string;
  ADVERTISER_CENTRE_URL: string;
  SUBSCRIPTIONS_API_URL: string;
  ENVIRONMENT: Environment;
  GPT_BASE_URL: string;
  GRAPHQL_API_URL: string;
  HEADER_SERVER_BASE: string;
  PUBLIC_PATH_BASE: string;
  HEAP_ANALYTICS_KEY: string;
  IMAGE_SERVER_BASE: string;
  INFORMANT_URL: string;
  SERVER_BASE_URL: string;
  SEGMENT_CDP_KEY: string;
  STRIPE_API_KEY: string;
  TRACKJS_TOKEN: string;
  NAVBAR_REDESIGN_EXPERIMENT_OPTIMIZE_ID: string;
}

const getCustomServerData = (): CustomServerData => {
  if (typeof window === 'undefined') {
    const nonBrowserDefault: CustomServerData = { environment: 'development' };
    return nonBrowserDefault;
  }

  const customServerDataElement = window.document.getElementById(
    CUSTOM_SERVER_DATA_ID,
  );
  if (!customServerDataElement || !customServerDataElement.textContent) {
    throw new Error(
      'A custom server data element should be present when rendering in the browser',
    );
  }
  return JSON.parse(customServerDataElement.textContent) as CustomServerData;
};

const getConfigValues = () => {
  const env = environment ?? getCustomServerData().environment;
  const values: Record<string, string | boolean> = {
    ENVIRONMENT: env,
  };

  for (const key of Object.keys(configVals)) {
    values[key] = configVals[key][env];
  }

  return values as any as FullConfig;
};

/**
 * @deprecated This value is only updated after the initial application render which is misleading. Use "getConfigValues" instead.
 */
const configValues: FullConfig = configValList as any as FullConfig;

export { setEnvironment, configValues, getConfigValues };
